import { useEffect, useMemo } from "react";

import { useLocation } from "react-router-dom";

import { EnvSwitchOverlay } from "./EnvSwitchOverlay";

/**
 * `<CrossOriginHandoff>` — render the branded loading overlay while
 * `window.location.assign(<targetOrigin>/<current-path>)` carries the
 * customer to a different portal origin (landing ↔ per-env portal).
 * Lifted from packages/landing's PostLoginEnvHandoff and
 * packages/portal's HandoffToLanding, which were identical except
 * for which origin they targeted.
 *
 * What this is FOR
 * ----------------
 * The CPF-NFR-028 no-visible-page-change UX contract. Cross-origin
 * navigation in the multi-Amplify topology means a full browser
 * page-change every time the customer crosses landing ↔ env-portal.
 * Without mitigation that's a 200-400 ms blank tab. With this
 * component the customer sees:
 *   1. The branded overlay paint (one RAF — layout commit)
 *   2. The overlay actually flush to screen (second RAF — paint
 *      commit; most browsers paint reliably after two RAFs)
 *   3. The cross-origin navigation start (only after paint)
 *   4. The destination portal's shell paints first
 *
 * The customer never sees a blank tab. They see the branded
 * loading state continuously across the entire transition.
 *
 * Path / search / hash preservation
 * ---------------------------------
 * The component derives the destination URL from
 * `targetOrigin + location.pathname + location.search + location.hash`
 * by default. That means a customer who deep-linked to
 * `clients.portal.tryrook.io/home/logs?user=abc#row-42` and gets
 * bounced (because they hit the wrong env's portal, or because their
 * session needs the landing's auth) lands at the EXACT same path on
 * the destination — bookmarks survive the topology.
 *
 * Override the derived path with `targetPath` only when the caller
 * needs to coerce a different destination (e.g., post-login handoff
 * that should always land on /home regardless of the URL the
 * customer clicked through).
 *
 * Why a build-time `targetOrigin` (vs deriving it at runtime)
 * -----------------------------------------------------------
 * Cross-origin navigation must NEVER trust runtime data for the
 * destination. An attacker who controls any query-string, cookie,
 * or DOM input could otherwise rewrite the target origin to a
 * phishing site that looks like the customer's portal. The
 * `targetOrigin` prop MUST come from a build-time constant
 * (PORTAL_ORIGINS in packages/shared/src/lib/portalOrigins.ts)
 * or from a server-validated env-assignment lookup. The component
 * itself does no validation — it trusts its caller chose a safe
 * origin. The allowlist is the caller's responsibility.
 *
 * Why the two-RAF guard (not setTimeout, not requestIdleCallback)
 * ---------------------------------------------------------------
 * - `setTimeout(0)` fires before paint and is the bug this guards
 *   against — the overlay would never reach the screen.
 * - `requestIdleCallback` is too lazy; it might wait hundreds of
 *   ms for an idle moment that never comes on a busy page.
 * - Two nested `requestAnimationFrame` calls is the right primitive:
 *   first RAF runs at the next layout commit, second RAF runs after
 *   the paint commit. After the second callback returns, the overlay
 *   pixels are on screen. Same pattern `EnvSwitchButton` uses for
 *   its click → navigate flow.
 */

export type CrossOriginHandoffProps = {
  /**
   * Destination portal origin (scheme + host + optional port — no
   * trailing slash, no path). MUST come from a build-time constant
   * or a server-validated lookup; the component does NO allowlist
   * check. Examples:
   *   "https://clients.portal.rook-connect.com"
   *   "https://production.clients.portal.tryrook.io"
   */
  targetOrigin: string;

  /**
   * Human-readable env label for the overlay text. Drives "Loading
   * <Production>..." copy. Examples: "Production", "Sandbox",
   * "Development", "Landing".
   */
  targetEnvLabel: string;

  /**
   * Optional explicit path to navigate to on the target origin.
   * Defaults to the current location (pathname + search + hash) so
   * deep links survive. Set to `"/home"` (or similar) when the
   * caller wants to coerce a specific destination regardless of
   * how the customer got here (e.g., post-login should always land
   * on the env-portal's /home, not at the landing's /).
   */
  targetPath?: string;

  /**
   * Optional spinner brand color for env-color emphasis per
   * CPF-UIR-089. Same default as EnvSwitchOverlay (ROOK purple).
   */
  brandColor?: string;

  /**
   * Test-only escape hatch — substitute the navigation function.
   * Production code MUST NOT pass this. jsdom-based unit tests
   * pass a mock here so they can assert "navigation would have
   * happened" without actually unloading the page. The default is
   * `(url) => window.location.assign(url)`.
   */
  navigateFn?: (url: string) => void;
};

export const CrossOriginHandoff = ({
  targetOrigin,
  targetEnvLabel,
  targetPath,
  brandColor,
  navigateFn,
}: CrossOriginHandoffProps) => {
  const location = useLocation();

  const targetUrl = useMemo(() => {
    if (targetPath !== undefined) {
      return `${targetOrigin}${targetPath}`;
    }
    return `${targetOrigin}${location.pathname}${location.search}${location.hash}`;
  }, [targetOrigin, targetPath, location.pathname, location.search, location.hash]);

  useEffect(() => {
    let cancelled = false;
    const navigate =
      navigateFn ?? ((url: string) => window.location.assign(url));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) {
          navigate(targetUrl);
        }
      });
    });
    return () => {
      cancelled = true;
    };
  }, [targetUrl, navigateFn]);

  return <EnvSwitchOverlay targetEnvLabel={targetEnvLabel} brandColor={brandColor} />;
};
